/**
 * Factories Redux Slice
 */

import { createSlice, type PayloadAction } from '@ufc/core'
import type {
  Factory,
  FactoryListItem,
  FactoryStats,
  FactoriesFilter,
  FactorySummary,
  CreateFactoryRequest,
  UpdateFactoryRequest,
  PaginationMeta,
  PaginatedFactories,
  Country,
  City,
  Location,
} from '../domain/types'
import { DEFAULT_PAGE_SIZE } from '../domain/constants'

// Storage key for persisting selected factory (for Gate Portal)
const STORAGE_KEY = 'gate-selected-factory'

export interface FactoriesState {
  items: FactoryListItem[]
  selectedFactory: Factory | null
  // Factory selection for header (Gate Portal)
  currentFactoryId: string | null
  currentFactory: FactorySummary | null
  factoriesList: FactorySummary[] // List for dropdown
  // Stats and filters
  stats: FactoryStats
  countries: Country[]
  cities: City[]
  citiesPage: number
  citiesHasMore: boolean
  citiesLoadMoreLoading: boolean
  citiesSearchResults: City[]
  citiesSearchLoading: boolean
  locations: Location[]
  filter: FactoriesFilter
  pagination: PaginationMeta
  loading: boolean
  error: string | null
}

function createDefaultFactoryStats(): FactoryStats {
  return { total: 0, active: 0, inactive: 0 }
}

function createDefaultFactoriesFilter(): FactoriesFilter {
  return { status: 'all', page: 1, pageSize: DEFAULT_PAGE_SIZE }
}

function createDefaultFactoriesPagination(): PaginationMeta {
  return { page: 1, pageSize: DEFAULT_PAGE_SIZE, pageCount: 1, total: 0 }
}

function resetFactoryListState(state: FactoriesState) {
  state.items = []
  state.selectedFactory = null
  state.stats = createDefaultFactoryStats()
  state.countries = []
  state.cities = []
  state.citiesPage = 1
  state.citiesHasMore = false
  state.citiesLoadMoreLoading = false
  state.citiesSearchResults = []
  state.citiesSearchLoading = false
  state.locations = []
  state.filter = createDefaultFactoriesFilter()
  state.pagination = createDefaultFactoriesPagination()
  state.loading = false
  state.error = null
}

const initialState: FactoriesState = {
  items: [],
  selectedFactory: null,
  // Factory selection for header
  currentFactoryId: null,
  currentFactory: null,
  factoriesList: [],
  // Stats and filters
  stats: createDefaultFactoryStats(),
  countries: [],
  cities: [],
  citiesPage: 1,
  citiesHasMore: false,
  citiesLoadMoreLoading: false,
  citiesSearchResults: [],
  citiesSearchLoading: false,
  locations: [],
  filter: createDefaultFactoriesFilter(),
  pagination: createDefaultFactoriesPagination(),
  loading: false,
  error: null,
}

export const factoriesSlice = createSlice({
  name: 'factories',
  initialState,
  reducers: {
    // Fetch actions
    fetchFactories: (state, _action: PayloadAction<FactoriesFilter | undefined>) => {
      state.loading = true
      state.error = null
    },
    fetchFactoriesSuccess: (state, action: PayloadAction<PaginatedFactories>) => {
      state.items = action.payload.data
      state.pagination = action.payload.pagination
      state.loading = false
      state.error = null
    },
    fetchFactoriesFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Fetch single factory
    fetchFactory: (state, _action: PayloadAction<string>) => {
      state.loading = true
      state.error = null
    },
    fetchFactorySuccess: (state, action: PayloadAction<Factory>) => {
      state.selectedFactory = action.payload
      state.loading = false
    },
    fetchFactoryFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Stats actions
    fetchStats: (state) => {
      state.loading = true
    },
    fetchStatsSuccess: (state, action: PayloadAction<FactoryStats>) => {
      state.stats = action.payload
      state.loading = false
    },
    fetchStatsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Countries actions
    fetchCountries: (state) => {
      state.loading = true
    },
    fetchCountriesSuccess: (state, action: PayloadAction<Country[]>) => {
      state.countries = action.payload
      state.loading = false
    },
    fetchCountriesFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Cities actions
    fetchCities: (state, _action: PayloadAction<string | undefined>) => {
      state.loading = true
    },
    fetchCitiesSuccess: (state, action: PayloadAction<{ data: City[]; hasMore: boolean }>) => {
      state.cities = action.payload.data
      state.citiesPage = 1
      state.citiesHasMore = action.payload.hasMore
      state.loading = false
    },
    fetchCitiesFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Load more cities (append next page)
    loadMoreCities: (state) => {
      state.citiesLoadMoreLoading = true
    },
    loadMoreCitiesSuccess: (state, action: PayloadAction<{ data: City[]; hasMore: boolean }>) => {
      state.cities = [...state.cities, ...action.payload.data]
      state.citiesPage = state.citiesPage + 1
      state.citiesHasMore = action.payload.hasMore
      state.citiesLoadMoreLoading = false
    },
    loadMoreCitiesFailure: (state) => {
      state.citiesLoadMoreLoading = false
    },

    // City search actions (for factory form drawer - backend search)
    searchCitiesInFactory: (state, _action: PayloadAction<string>) => {
      state.citiesSearchLoading = true
    },
    searchCitiesInFactorySuccess: (state, action: PayloadAction<City[]>) => {
      state.citiesSearchResults = action.payload
      state.citiesSearchLoading = false
    },
    searchCitiesInFactoryFailure: (state, action: PayloadAction<string>) => {
      state.citiesSearchLoading = false
    },
    clearCitiesSearch: (state) => {
      state.citiesSearchResults = []
      state.citiesSearchLoading = false
    },

    // Locations actions (from addressbook)
    fetchLocations: (state, _action: PayloadAction<string | undefined>) => {
      state.loading = true
    },
    fetchLocationsSuccess: (state, action: PayloadAction<Location[]>) => {
      state.locations = action.payload
      state.loading = false
    },
    fetchLocationsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Create actions
    createFactory: (state, _action: PayloadAction<CreateFactoryRequest>) => {
      state.loading = true
      state.error = null
    },
    createFactorySuccess: (state, action: PayloadAction<FactoryListItem>) => {
      state.items = [...state.items, action.payload]
      state.loading = false
    },
    createFactoryFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Update actions
    updateFactory: (
      state,
      _action: PayloadAction<{ documentId: string; data: UpdateFactoryRequest }>
    ) => {
      state.loading = true
      state.error = null
    },
    updateFactorySuccess: (state, action: PayloadAction<FactoryListItem>) => {
      state.items = state.items.map((factory) =>
        factory.documentId === action.payload.documentId ? action.payload : factory
      )
      state.loading = false
    },
    updateFactoryFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Delete actions
    deleteFactory: (state, _action: PayloadAction<string>) => {
      state.loading = true
      state.error = null
    },
    deleteFactorySuccess: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((factory) => factory.documentId !== action.payload)
      if (state.selectedFactory?.documentId === action.payload) {
        state.selectedFactory = null
      }
      state.loading = false
    },
    deleteFactoryFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // State setters
    setFactories: (state, action: PayloadAction<FactoryListItem[]>) => {
      state.items = action.payload
      state.loading = false
      state.error = null
    },
    setSelectedFactory: (state, action: PayloadAction<Factory | null>) => {
      state.selectedFactory = action.payload
    },
    setStats: (state, action: PayloadAction<FactoryStats>) => {
      state.stats = action.payload
    },
    setFilter: (state, action: PayloadAction<FactoriesFilter>) => {
      state.filter = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
      state.loading = false
    },

    // Search and filter actions
    searchFactories: (state, action: PayloadAction<string>) => {
      state.filter = { ...state.filter, search: action.payload, page: 1 }
    },
    filterByStatus: (state, action: PayloadAction<FactoriesFilter['status']>) => {
      state.filter = { ...state.filter, status: action.payload, page: 1 }
    },

    // Pagination actions
    setPage: (state, action: PayloadAction<number>) => {
      state.filter = { ...state.filter, page: action.payload }
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.filter = { ...state.filter, pageSize: action.payload, page: 1 }
    },

    // Clear state
    clearFactories: (state) => {
      state.items = []
      state.selectedFactory = null
      state.error = null
    },
    clearError: (state) => {
      state.error = null
    },

    // ========================================================================
    // Factory Selection (for Gate Portal header)
    // ========================================================================

    /**
     * Fetch factories for the header dropdown
     */
    fetchFactoriesList: (state) => {
      state.loading = true
      state.error = null
    },
    fetchFactoriesListSuccess: (state, action: PayloadAction<FactorySummary[]>) => {
      state.factoriesList = action.payload
      state.loading = false
    },
    fetchFactoriesListFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    /**
     * Set the list of available factories for the dropdown
     */
    setFactoriesList: (state, action: PayloadAction<FactorySummary[]>) => {
      state.factoriesList = action.payload
    },

    /**
     * Select a factory (or null for "All Factories" view)
     * Persists to localStorage for page refresh
     */
    selectFactory: (state, action: PayloadAction<FactorySummary | null>) => {
      resetFactoryListState(state)
      state.currentFactory = action.payload
      state.currentFactoryId = action.payload?.documentId ?? null

      // Persist to localStorage for page refresh
      if (typeof window !== 'undefined') {
        if (action.payload) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(action.payload))
        } else {
          localStorage.removeItem(STORAGE_KEY)
        }
      }
    },

    /**
     * Clear factory selection (return to "All Factories" view)
     */
    clearFactorySelection: (state) => {
      resetFactoryListState(state)
      state.currentFactory = null
      state.currentFactoryId = null

      if (typeof window !== 'undefined') {
        localStorage.removeItem(STORAGE_KEY)
      }
    },

    /**
     * Restore factory selection from localStorage
     * Should be called on app initialization
     */
    restoreFactoryFromStorage: (state) => {
      if (typeof window === 'undefined') return

      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        try {
          const factory = JSON.parse(stored) as FactorySummary
          state.currentFactory = factory
          state.currentFactoryId = factory.documentId
        } catch {
          localStorage.removeItem(STORAGE_KEY)
        }
      }
    },

    /**
     * Reset factory context to initial state
     */
    resetFactoryContext: (state) => {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(STORAGE_KEY)
      }
      resetFactoryListState(state)
      state.currentFactory = null
      state.currentFactoryId = null
      state.factoriesList = []
    },
  },
})

export const factoriesActions = factoriesSlice.actions
export const factoriesReducer = factoriesSlice.reducer
